import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { MrpSkuWithUnitLabel } from "@/components/mrp/MrpSkuWithUnitLabel";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import type { MrpSkuHoverHintFields } from "@/lib/mrp";

import {
  formatBomEachQuantity,
  formatCount,
  type WorkOrderBomLine,
} from "./workOrdersPageModel";

function WorkOrderBomComponentCell({
  componentSku,
  hint,
  hintsLoading,
  onHoverIntent,
}: {
  componentSku: string;
  hint: MrpSkuHoverHintFields | undefined;
  hintsLoading: boolean;
  onHoverIntent: () => void;
}) {
  return (
    <HoverHintTrigger
      align="start"
      block
      trigger={
        <span
          className="block min-w-0 truncate cursor-default font-mono text-xs text-sky-800 underline decoration-sky-800/40"
          onPointerEnter={onHoverIntent}
          onFocus={onHoverIntent}
        >
          <MrpSkuWithUnitLabel sku={componentSku} unit={hint?.mv_unit} />
        </span>
      }
    >
      <MrpSkuCatalogHoverHintBody
        sku={componentSku}
        hint={hint}
        loading={hintsLoading}
      />
    </HoverHintTrigger>
  );
}

export function WorkOrderCurrentBomTable({ bom }: { bom: WorkOrderBomLine[] }) {
  const componentSkus = useMemo(
    () => [...new Set(bom.map((line) => line.componentSku.trim()).filter((sku) => sku.length > 0))],
    [bom],
  );

  const [hintsFetchEnabled, setHintsFetchEnabled] = useState(false);
  const enableHintsFetch = useCallback(() => {
    setHintsFetchEnabled(true);
  }, []);

  useEffect(() => {
    if (componentSkus.length === 0) {
      return;
    }
    const idleId =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback(() => setHintsFetchEnabled(true))
        : window.setTimeout(() => setHintsFetchEnabled(true), 0);
    return () => {
      if (typeof cancelIdleCallback === "function" && typeof idleId === "number") {
        cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId as number);
      }
    };
  }, [componentSkus]);

  const skuHoverHintsQuery = useQuery<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }, ApiError>({
    queryKey: ["mrp", "sku-hover-hints", "work-order-detail", componentSkus.join("\n")],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: componentSkus.join(",") },
      }),
    enabled: hintsFetchEnabled && componentSkus.length > 0,
    staleTime: 60_000,
  });

  const skuHoverHints = skuHoverHintsQuery.data?.sku_hover_hints;
  const hintsLoading =
    hintsFetchEnabled && (skuHoverHintsQuery.isPending || skuHoverHintsQuery.isFetching);

  if (bom.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-500">
        No component lines on this work order.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <div
        className="max-h-72 overflow-auto"
        onPointerEnter={enableHintsFetch}
      >
        <table className="min-w-full border-separate border-spacing-0 text-xs">
          <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-[11px] uppercase text-slate-600 shadow-[0_1px_0_0_rgb(226_232_240)]">
            <tr>
              <th className="px-2 py-1.5 text-left">Component</th>
              <th className="px-2 py-1.5 text-right">Each</th>
              <th className="px-2 py-1.5 text-right">Total</th>
              <th className="px-2 py-1.5 text-right">Allocated</th>
              <th className="px-2 py-1.5 text-right">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {bom.map((line) => (
              <tr key={`${line.id}:${line.componentSku}`} className="border-b border-slate-100 last:border-b-0">
                <td className="max-w-0 px-2 py-1.5">
                <WorkOrderBomComponentCell
                  componentSku={line.componentSku}
                  hint={skuHoverHints?.[line.componentSku.trim()]}
                  hintsLoading={hintsLoading}
                  onHoverIntent={enableHintsFetch}
                />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatBomEachQuantity(line.quantityPerFinishedGood)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatCount(line.quantityOrdered)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatCount(line.quantityAllocated)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatCount(line.quantityRemaining)}</td>
            </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
